import Button, { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconProps } from "@insite/mobius/Icon";
import { MappedLink } from "@insite/mobius/Menu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import React from "react";
import { connect, ConnectedProps, ResolveThunks, useDispatch } from "react-redux";
import styled, { css, ThemeConsumer, ThemeProps } from "styled-components";
import ADILiveChatComponent from "./ADILiveChatComponent";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps, StyledButton } from "@insite/mobius/Clickable";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import toggleRewardsDrawer from "@NA/Handlers/ADIRewardsDrawerHandler";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getShipToState, getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import { MaNavSettingSettings } from "@NA/Store/CustomTypeModel/CustomSettingCollectionModel";

export interface MyAccountPanelMenuPresentationProps {
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MyAccountPanelMenuPresentationProps>;
        children?: StyledProp<MyAccountPanelMenuPresentationProps>;
        menu?: StyledProp<MyAccountPanelMenuPresentationProps>;
    };
    /** Props passed to the typography children of the menu.
     * @themable */
    childTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu close icon.
     * @themable */
    closeIconProps?: IconProps;
    /** Props passed to the menu back icon.
     * @themable */
    backIconProps?: IconProps;
    /** Color of the background of the menu header.
     * @themable */
    headerColor?: string;
    /** Color of the background of the menu body.
     * @themable */
    bodyColor?: string;
    /** Props to be passed into the panel row component.
     * @themable */
    panelRowProps?: PanelRowPresentationProps;
}

export type MyAccountPanelMenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** The element that triggers the menu. */
        panelTrigger: React.ReactElement;
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /**
         * The nested child structure that will be rendered by the menu.
         * If menu items are provided, children organically passed to the component
         * will not be rendered.
         */
        menuItems?: MappedLink[];
        /** Maximum number of menus that will appear in submenu panels. */
        maxDepth?: number;
        /** Function called to close overlay (Drawer) in which menu resides. Governs presence of `x` icons in headers. */
        closeOverlay?: () => void;
        dispatch?: any;
        /** The current URL of the application, used to apply 'current page' styles to the menu item */
        currentUrl?: string;
        /** @ignore internal prop */
        layer: number;
        billToState?: any;
        shipToState?: any;
        userName?: string;
        currentUserIsGuest?: boolean;
        isPunchOutSession?: boolean;
        onSignOutHandler?: any;
        changeCustomerMethod?: any;
        show?: MaNavSettingSettings;
        displayChangeCustomerLink: boolean;
    }
>;

const MyAccountPanelMenuWrapper = styled.nav<ThemeProps<BaseTheme> & InjectableCss>`
    button,
    link {
        &:focus {
            outline-offset: -${getProp("theme.focus.width")};
        }
    }
    > button {
        width: 100%;
    }
    ${injectCss}
`;

const MyAccountPanelMenuStyle = styled.div<ThemeProps<BaseTheme> & InjectableCss & { color?: string }>`
    height: calc(100% - 50px);
    overflow-y: auto;
    margin-top: -1px;
    background: ${({ color, theme }) => resolveColor(color, theme)};
    ${injectCss}
`;

const PanelChildren = styled.div<ThemeProps<BaseTheme> & InjectableCss & { displayLayer: number }>`
    max-height: 100vh;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 100%;
    z-index: 1;
    transition: all ease-in-out ${getProp("theme.transition.duration.short")}ms;
    ${({ displayLayer }) => {
        if (displayLayer <= 0) {
            return css`
                left: 0;
            `;
        }
        return css`
            left: 100%;
        `;
    }}
    ${injectCss}
`;

const DrawerHeaderButtonWrapper = styled.div<any>`
    padding: 5px 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: bold;
    gap: 5px;
`;
const BackButtonWrapper = styled.div<any>`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

/** MyAccountPanelMenuProps with defaulted properties now listed as not-optional, plus theme. */
export type MyAccountPanelMenuProps = MyAccountPanelMenuComponentProps & MyAccountPanelMenuPresentationProps;

type MyAccountPanelMenuPropsCompleted = MyAccountPanelMenuPresentationProps &
    Omit<MyAccountPanelMenuComponentProps, "maxDepth"> &
    Required<Pick<MyAccountPanelMenuComponentProps, "maxDepth">>;

export interface MyAccountPannelMenuStyles {
    liveChatWrapper?: InjectableCss;
    myAccountHeading?: InjectableCss;
    myAccountMenuTitle?: InjectableCss;
    myAccountMenuGridContainer?: GridContainerProps;
    myAccountMenuGridItem?: GridItemProps;
    myAccountMenuLink?: LinkPresentationProps;
    chatWithUs?: InjectableCss;
    chatWithUsLink?: LinkPresentationProps;
    divider?: InjectableCss;
    addressInfo?: InjectableCss;
    customerNumber?: InjectableCss;
    signOutClickable?: ClickablePresentationProps;
    accountHeadingSection?: InjectableCss;
}

export const myAccountPannelMenuStyles: MyAccountPannelMenuStyles = {
    liveChatWrapper: {
        css: css`
            margin-top: 20px;
        `,
    },
    myAccountHeading: {
        css: css`
            font-size: 24px;
            font-weight: bold;
            color: ${ADIColors.darkBlack3};
            margin: 20px 0 15px 20px;
            margin-bottom: 0px !important;
        `,
    },
    myAccountMenuTitle: {
        css: css`
            font-size: 24px;
            margin: 20px 0;
            font-weight: bold;
            cursor: default;
            line-height: 1.43;
            width: 100%;
            overflow: hidden;
            padding: 0 20px;
            color: ${getColor("text.main")};
        `,
    },
    myAccountMenuGridContainer: {
        css: css`
            font-size: 24px;
            margin: 20px 0;
            font-weight: bold;
            cursor: default;
            line-height: 1.43;
            width: 100%;
            overflow: hidden;
            padding: 0 20px;
            margin-top: 10px !important;
        `,
        offsetCss: css`
            flex-direction: column;
            margin: 0 auto;
            list-style: none;
            margin: 0;
            width: 100%;
            float: left;
            overflow: hidden;
            background: ${getColor("common.background")};
        `,
    },
    myAccountMenuGridItem: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            padding: 0px;
            list-style: none;
            width: 100%;
            float: left;
            margin: 0 0 8px !important;
            cursor: default;
            line-height: normal;
            font-size: 12px;
            font-weight: 600;
        `,
    },
    myAccountMenuLink: {
        css: css`
            outline: none;
            > span {
                color: ${getColor("text.main")} !important;
                font-weight: 600;
            }
            &:hover {
                > span {
                    color: ${getColor("text.main")} !important;
                    text-decoration: underline;
                }
            }
        `,
    },
    chatWithUs: {
        css: css`
            width: 100%;
            background: ${getColor("common.accent")};
            text-align: center;
            cursor: auto;
        `,
    },
    chatWithUsLink: {
        css: css`
            font-size: 12px;
            font-weight: bold;
            padding: 23px 0;
            display: block;
            line-height: normal;
            width: 100%;
            > span {
                color: ${getColor("text.main")} !important;
                &:hover {
                    text-decoration: underline;
                }
            }
        `,
    },
    divider: {
        css: css`
            border-top: 1px solid ${getColor("common.border")};
            margin: 0 20px;
        `,
    },
    customerNumber: {
        css: css`
            padding-bottom: 0px;
            flex-basis: 100%;
            max-width: 100%;
            padding: 0px 10px 0px 0px;
            color: ${getColor("text.main")};
            font-size: 12px;
            font-weight: 500;
            line-height: normal;
            margin-top: 0;
            button {
                padding: 0 !important;
            }
        `,
    },
    accountHeadingSection: {
        css: css`
            all: unset;
            font-weight: bold;
        `,
    },
    addressInfo: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            padding: 0px;
            color: ${getColor("text.main")};
            font-size: 12px;
            font-weight: 600;
            line-height: normal;
            margin-top: 0;
            text-transform: capitalize;
        `,
    },
    signOutClickable: {
        css: css`
            width: 100%;
            font-size: 14px;
            min-height: 68px;
            padding: 0 20px;
            color: ${getColor("primary")};
            display: block;
            box-sizing: border-box;
            margin-bottom: 1rem !important;
            margin-top: 20px !important;
            font-weight: 600;
            outline: none;
            border-top: 1px solid ${getColor("common.border")};
            margin: 0px !important;
            border-bottom: none;
            padding: 20px 20px 39px !important;
            &:hover {
                text-decoration: underline;
            }
        `,
    },
};

const styles = myAccountPannelMenuStyles;

const closeModal = () => {
    toggleLoginDrawer({ loginDrawer: false });
};

function setVisibilityRoles(show: MaNavSettingSettings | undefined, roles: string[]) {
    if (show) {
        if (show.showMyAccount) {
            roles.push("Account");
        }

        if (show.showInvoices) {
            roles.push("Invoices");
        }

        if (show.showOrder) {
            roles.push("My Orders");
        }

        if (show.showReorder) {
            // we used Reorder Access for Recently Purchased Products
            roles.push("Recently Purchased Products");
        }
        if (show.showQuote) {
            roles.push("My Quotes");
        }
        // My Account slide out story changes to remove
        // if (show.showRequestaQuote) roles.push("Request Quote Form");

        if (show.showQuickOrder) {
            roles.push("Quick Order");
        }

        if (show.showMyLists) {
            roles.push("My Lists");
        }

        if (show.showViewStatements) {
            roles.push("View My Statements");
        }
    }
}

class MyAccountPanelMenu extends React.Component<MyAccountPanelMenuPropsCompleted> {
    static defaultProps = { maxDepth: 3 };

    state = {
        layer: this.props.layer + 1,
        descriptionId: this.props.descriptionId || uniqueId(),
        show: this.props.show,
    };

    UNSAFE_componentWillReceiveProps(nextProps: MyAccountPanelMenuPropsCompleted) {
        // eslint-disable-line camelcase
        if (nextProps.layer !== this.props.layer + 1 && !nextProps.children && !this.props.children) {
            this.setState({ layer: nextProps.layer + 1 }, () => { });
        }
    }

    moveNextLeft = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer - 1 };
        });
    };

    moveNextRight = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer + 1 };
        });
    };

    renderMenuItems(childMenuItems: MappedLink[], currentDepth: number, propBuilder: any) {
        const { show } = this.props;
        const roles: Array<string> = [];
        const { applyProp } = propBuilder;
        const { currentUrl } = this.props;
        const typographyProps = applyProp("childTypographyProps");
        const panelRowProps = applyProp("panelRowProps");

        setVisibilityRoles(show, roles);

        return (
            <>
                {childMenuItems
                    .filter(
                        item =>
                            item?.type == "QuickOrderPage" ||
                            (!item.excludeFromNavigation &&
                                ![
                                    "AddressesPage",
                                    "ChangeCustomerPage",
                                    "OrderApprovalListPage",
                                    "SavedOrderListPage",
                                    "UserListPage",
                                    "ADIAdminPage",
                                ].includes(item.type!)),
                    )
                    .map(item => {
                        // if (item.title != "ADI Pro Rewards" && !roles.includes(item.title.trim())) {
                        //     return null;
                        // }
                        const renderChildren =
                            item.children?.filter(child => !child.excludeFromNavigation).length &&
                            currentDepth < this.props.maxDepth;
                        const clickableProps: { href?: string } = { ...item.clickableProps };
                        if (!renderChildren && item.url) {
                            clickableProps.href = item.url;
                        }

                        const menuItem = (
                            <PanelRow
                                key={item.title}
                                isCurrent={currentUrl === item.url}
                                onClick={() => {
                                    if (item.title === "ADI Pro Rewards") {
                                        this.props.dispatch({
                                            type: "AccountPage/ProRewardsUpdate",
                                        });
                                    }
                                    if (this.props.closeOverlay) {
                                        this.props.closeOverlay();
                                    }
                                }}
                                color={applyProp("bodyColor")}
                                hasChildren={renderChildren || false}
                                tabIndex={this.state.layer === 0 ? 0 : -1}
                                {...clickableProps}
                                {...panelRowProps}
                            >
                                <Typography
                                    data-test-selector={`${this.props["data-test-selector"]}_${item.title}`}
                                    weight="bold"
                                    size={15}
                                    {...typographyProps}
                                    style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                                >
                                    {item.title.toLowerCase() === "my lists"
                                        ? "My Lists"
                                        : item.title.toLowerCase() === "my orders"
                                            ? "My Orders"
                                            : item.title.toLowerCase().trim() === "my quotes"
                                                ? "My Quotes"
                                                : item.title.toLowerCase() === "account"
                                                    ? "Account Dashboard"
                                                    : item.title.toLowerCase().trim() === "recently purchased products"
                                                        ? "Recently Purchased"
                                                        : item.title.toLowerCase() === "view my statements"
                                                            ? "Statements"
                                                            : item.title.toLowerCase() === "request quote form"
                                                                ? "Request a Quote"
                                                                : item.title.toLowerCase() === "xtensignup"
                                                                    ? null
                                                                    : item.title}
                                </Typography>
                            </PanelRow>
                        );
                        if (renderChildren) {
                            return (
                                <MyAccountPanelMenu
                                    {...this.props}
                                    as="div"
                                    key={item.title}
                                    panelTrigger={menuItem}
                                    menuItems={item.children as MappedLink[]}
                                    maxDepth={this.props.maxDepth - 1}
                                    layer={this.state.layer}
                                />
                            );
                        }
                        return menuItem;
                    })}
                <StyledWrapper style={{ marginTop: "20px" }}>
                    {this.props.userName && !this.props.currentUserIsGuest && (
                        <Clickable
                            {...styles.signOutClickable}
                            onClick={this.props.onSignOutHandler}
                            data-test-selector="headerSignOutLink"
                        >
                            {this.props.isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                        </Clickable>
                    )}
                </StyledWrapper>
            </>
        );
    }

    render() {
        const { show } = this.props;
        const roles: Array<string> = [];

        setVisibilityRoles(show, roles);

        return (
            <ThemeConsumer>
                {theme => {
                    const { currentUrl, descriptionId, menuItems, panelTrigger, closeOverlay, ...otherProps } =
                        this.props;
                    const propBuilder = applyPropBuilder({ ...this.props, theme }, { component: "panelMenu" });
                    const { translate } = theme;
                    const { layer, descriptionId: navId } = this.state;
                    const { spreadProps, applyProp } = propBuilder;
                    const cssOverrides = spreadProps("cssOverrides");

                    return (
                        <MyAccountPanelMenuWrapper
                            css={cssOverrides?.wrapper}
                            {...{ "aria-labelledby": navId }}
                            {...otherProps}
                            tabIndex={-1}
                        >
                            {React.cloneElement(panelTrigger, { id: navId, onClick: this.moveNextLeft })}
                            <PanelChildren
                                displayLayer={layer}
                                aria-hidden={layer > 0}
                                css={cssOverrides?.children}
                                data-layer={layer}
                            >
                                <PanelRow
                                    style={{ paddingTop: "15px", backgroundColor: (this.props.userName && !this.props.currentUserIsGuest) ? "#f7f7f7" : ADIColors.white }}
                                    as="div"
                                    header
                                >
                                    <DrawerHeaderButtonWrapper
                                        style={{
                                            width: "calc(100% - 55px)",
                                            justifyContent: "left",
                                            background: (this.props.userName && !this.props.currentUserIsGuest) ? "#f7f7f7" : "#fff",
                                            fontSize: "14px",
                                            color: ADIColors.darkBlack3,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        <ButtonIcon {...spreadProps("backIconProps")} onClick={this.moveNextRight} />
                                        <BackButtonWrapper>Back to Main Menu</BackButtonWrapper>
                                        <VisuallyHidden>{translate("back")}</VisuallyHidden>
                                    </DrawerHeaderButtonWrapper>
                                    {closeOverlay && (
                                        <DrawerHeaderButtonWrapper>
                                            <ButtonIcon {...spreadProps("closeIconProps")} onClick={closeOverlay} />
                                            <VisuallyHidden>{translate("close")}</VisuallyHidden>
                                        </DrawerHeaderButtonWrapper>
                                    )}
                                </PanelRow>
                                <MyAccountPanelMenuStyle
                                    css={cssOverrides?.menu}
                                    color={applyProp("bodyColor", "common.accent")}
                                    style={{ backgroundColor: (this.props.userName && !this.props.currentUserIsGuest) ? "#f7f7f7" : "", flex: "1", height: "auto", maxHeight: "fit-content" }}
                                >
                                    <StyledWrapper {...styles.myAccountHeading}>
                                        {translate("My Account")}
                                    </StyledWrapper>
                                    <GridContainer
                                        className="myAccountMenuGridContainer"
                                        data-test-selector="myAccountMenuGridContainer"
                                        {...styles.myAccountMenuGridContainer}
                                        as="ul"
                                    >
                                        <GridItem className="myAccountMenuGridItem" {...styles.customerNumber} as="li" style={{ backgroundColor: (this.props.userName && !this.props.currentUserIsGuest) ? "#f7f7f7" : "" }}>
                                            <Typography><strong {...styles.accountHeadingSection}>Account # {this.props.billToState.value?.customerNumber}</strong></Typography>
                                            {this.props.displayChangeCustomerLink && (
                                                <><Typography>&nbsp;&nbsp;|&nbsp;&nbsp;</Typography><Link onClick={this.props.changeCustomerMethod} style={{ backgroundColor: (this.props.userName && !this.props.currentUserIsGuest) ? "#f7f7f7" : "", marginBottom: (this.props.userName && !this.props.currentUserIsGuest) ? "20px" : ""}}>{translate("Change")}</Link></>
                                            )}
                                        </GridItem>
                                        <GridItem className="myAccountMenuGridItem" {...styles.addressInfo} as="li" style={{ backgroundColor: (this.props.userName && !this.props.currentUserIsGuest) ? "#f7f7f7" : "" }}>
                                            {this.props.billToState.value?.companyName}
                                        </GridItem>
                                        <GridItem className="myAccountMenuGridItem" {...styles.addressInfo} as="li" style={{ backgroundColor: (this.props.userName && !this.props.currentUserIsGuest) ? "#f7f7f7" : "" }}>
                                            {this.props.shipToState.value?.address1}
                                        </GridItem>
                                        <GridItem className="myAccountMenuGridItem" {...styles.addressInfo} as="li" style={{ backgroundColor: (this.props.userName && !this.props.currentUserIsGuest) ? "#f7f7f7" : "" }}>
                                            {this.props.shipToState.value?.city},&nbsp;{this.props.shipToState.value?.state?.name}&nbsp;
                                            {this.props.shipToState.value?.postalCode}
                                        </GridItem>
                                    </GridContainer>
                                    <StyledWrapper style={{ flex: "1 1 100%", width: "100%", backgroundColor: "#fff", paddingTop: "15px", height: "100vh", }}>
                                        {menuItems && this.renderMenuItems(menuItems, 1, propBuilder)}
                                    </StyledWrapper>
                                </MyAccountPanelMenuStyle>
                            </PanelChildren>
                        </MyAccountPanelMenuWrapper>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/**
 * Menu composed of slide-in panels.
 */
// TODO ISC-11903
// export const PanelMenu = (props: PanelMenuProps) => (<PanelMenuCC {...props as PanelMenuPropsCompleted} />);

MyAccountPanelMenu.defaultProps = {
    maxDepth: 3,
};

/** @component */
export default MyAccountPanelMenu;
